import React, { useEffect, useState } from "react";
import {
  Table, Tag, Input, Button, Modal, message, Form,
  Select, Space, Tooltip, Segmented, Divider
} from "antd";
import {
  UsergroupAddOutlined, EyeOutlined,
  DeleteOutlined, PlusOutlined, CrownOutlined,
  CodeOutlined, UserOutlined, LockOutlined,
  MailOutlined, PhoneOutlined,
  ReloadOutlined, TeamOutlined, CheckCircleOutlined,
  DownOutlined, UpOutlined
} from "@ant-design/icons";
import { UserGet, UserRemove, UserPost, UserUpdate } from "../../../../Api/CoreApi";
import "./AdminUsers.css";

const { Option } = Select;

function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(false);
  const [roleFilter, setRoleFilter] = useState("all");

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [addLoading, setAddLoading] = useState(false);
  const [form] = Form.useForm();

  // 📱 Mobile State
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showStats, setShowStats] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await UserGet();
      const filtered = Array.isArray(response)
        ? response.filter(u => u.role === "admin" || u.role === "developer")
        : [];
      setUsers(filtered);
      setFiltered(filtered);
    } catch (err) {
      message.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  useEffect(() => {
    let data = [...users];
    if (roleFilter !== "all") {
      data = data.filter(u => u.role === roleFilter);
    }
    setFiltered(data);
  }, [roleFilter, users]);

  const developerExists = users.some(u => u.role === 'developer');

  const handleAddUser = async (values) => {
    try {
      if (developerExists) {
        message.error("❌ Only 1 Developer can be created!");
        return;
      }
      setAddLoading(true);
      const payload = {
        username: values.username.trim(),
        password: values.password,
        email: values.email || "",
        first_name: values.first_name || "",
        contact: values.contact || "",
        role: "developer",
        is_active: true,
      };
      await UserPost(payload);
      message.success(`✅ Developer added successfully!`);
      form.resetFields();
      setAddModalOpen(false);
      fetchUsers();
    } catch (err) {
      const errMsg = err?.response?.data?.username?.[0] || err?.response?.data?.error || "Failed to add user";
      message.error(errMsg);
    } finally {
      setAddLoading(false);
    }
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: `Delete ${record.role === 'admin' ? 'Admin' : 'Developer'}?`,
      content: `${record.first_name || record.username} will be permanently deleted.`,
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        try {
          await UserRemove(record.id);
          message.success("User deleted!");
          fetchUsers();
        } catch {
          message.error("Failed to delete");
        }
      },
    });
  };

  const handleView = (user) => {
    setSelectedUser(user);
    setViewModalOpen(true);
  };

  const stats = {
    total: users.length,
    admins: users.filter(u => u.role === 'admin').length,
    developers: users.filter(u => u.role === 'developer').length,
    active: users.filter(u => u.is_active).length,
  };

  const columns = [
    { title: "S.No.", key: "sno", width: 60, render: (_, __, i) => <b>{i + 1}</b> },
    {
      title: "Profile ID", key: "id", width: 130,
      render: (_, r) => (
        <span style={{ color: r.role === 'admin' ? '#ff0055' : '#8b5cf6', fontWeight: 700, fontSize: 13, fontFamily: 'monospace' }}>
          {r.profile_id || r.id?.substring(0, 8)}
        </span>
      ),
    },
    {
      title: "Name", key: "name", width: 200,
      render: (_, r) => (
        <div className="admin-name-cell">
          <div className="admin-avatar" style={{
            background: r.role === 'admin'
              ? 'linear-gradient(135deg, #ff0055, #ff4d88)'
              : 'linear-gradient(135deg, #8b5cf6, #a78bfa)'
          }}>
            {r.first_name?.[0] || r.username?.[0] || "U"}
          </div>
          <div>
            <div style={{ fontWeight: 700 }}>{r.first_name || "N/A"}</div>
            <div style={{ fontSize: 11, color: '#94a3b8' }}>@{r.username}</div>
          </div>
        </div>
      ),
    },
    { title: "Email", dataIndex: "email", key: "email", render: (email) => email || <span style={{ color: '#94a3b8' }}>N/A</span> },
    { title: "Contact", dataIndex: "contact", key: "contact", render: (c) => c || <span style={{ color: '#94a3b8' }}>N/A</span> },
    {
      title: "Role", key: "role", width: 120,
      render: (_, r) => {
        if (r.role === 'admin') return <Tag icon={<CrownOutlined />} color="magenta" style={{ fontWeight: 700 }}>Admin</Tag>;
        if (r.role === 'developer') return <Tag icon={<CodeOutlined />} color="purple" style={{ fontWeight: 700 }}>Developer</Tag>;
        return <Tag>{r.role}</Tag>;
      },
    },
    { title: "Status", key: "status", width: 100, render: (_, r) => <Tag color={r.is_active ? "green" : "orange"}>{r.is_active ? "● Active" : "○ Inactive"}</Tag> },
    {
      title: "Action", key: "action", width: 130, fixed: 'right',
      render: (_, record) => (
        <Space size={5}>
          <Tooltip title="View Details">
            <Button size="small" type="primary" icon={<EyeOutlined />}
              style={{ background: record.role === 'admin' ? '#ff0055' : '#8b5cf6', borderColor: record.role === 'admin' ? '#ff0055' : '#8b5cf6' }}
              onClick={() => handleView(record)} />
          </Tooltip>
          <Tooltip title="Delete">
            <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // ============================================
  // MOBILE CARD RENDERER
  // ============================================
  const renderMobileCard = (r) => (
    <div key={r.id} className="mobile-admin-card">
      <div className="mac-header">
        <span className={`mac-role-tag ${r.role}`}>
          {r.role === 'admin' ? <><CrownOutlined /> Admin</> : <><CodeOutlined /> Developer</>}
        </span>
        <Tag color={r.is_active ? "green" : "orange"} style={{ margin: 0, borderRadius: 12 }}>
          {r.is_active ? "● Active" : "○ Inactive"}
        </Tag>
      </div>

      <div className="mac-body" onClick={() => handleView(r)}>
        <div className="mac-avatar" style={{
          background: r.role === 'admin' ? 'linear-gradient(135deg, #ff0055, #ff4d88)' : 'linear-gradient(135deg, #8b5cf6, #a78bfa)'
        }}>
          {r.first_name?.[0] || r.username?.[0] || "U"}
        </div>
        <div className="mac-info">
          <h4>{r.first_name || "N/A"}</h4>
          <span className="mac-username">@{r.username}</span>
          <div className="mac-meta">
            {r.email && <span><MailOutlined /> {r.email}</span>}
            {r.contact && <span><PhoneOutlined /> {r.contact}</span>}
          </div>
        </div>
      </div>

      <div className="mac-footer">
        <span className="mac-profile-id" style={{ color: r.role === 'admin' ? '#ff0055' : '#8b5cf6' }}>
          {r.profile_id || r.id?.substring(0, 8)}
        </span>
        <div className="mac-actions">
          <button className="mac-btn view" onClick={(e) => { e.stopPropagation(); handleView(r); }}>
            <EyeOutlined />
          </button>
          <button className="mac-btn delete" onClick={(e) => { e.stopPropagation(); handleDelete(r); }}>
            <DeleteOutlined />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="admin-users-page">

      {/* ============ HEADER + STATS WRAPPER ============ */}
      <div className="admin-users-header-wrapper">
        <div className="page-header">
          <div className="header-left">
            <div className="title-icon"><UsergroupAddOutlined /></div>
            <div className="title-content">
              <h2 className="page-title">Admin & Developer</h2>
              <p className="page-subtitle">
                Manage system administrators and developers
              </p>
            </div>
          </div>

          <div className="header-actions">
            <Tooltip title="Refresh">
              <Button icon={<ReloadOutlined />} onClick={fetchUsers} loading={loading} className="icon-mobile-btn action-btn-refresh" />
            </Tooltip>
            {!developerExists && (
              <Button
                type="primary" icon={<PlusOutlined />}
                className="add-dev-btn icon-mobile-btn"
                onClick={() => setAddModalOpen(true)}
              >
                <span className="hide-on-mobile">Add Developer</span>
              </Button>
            )}
            {isMobile && (
              <Button 
                className="toggle-stats-btn icon-mobile-btn" 
                onClick={() => setShowStats(!showStats)}
                icon={showStats ? <UpOutlined /> : <DownOutlined />}
              />
            )}
          </div>
        </div>

        {/* ============ STATS GRID ============ */}
        <div className={`stats-grid-wrapper ${!isMobile || showStats ? 'show' : 'hide'}`}>
          <div className="stat-item total">
            <div className="stat-icon-wrap"><TeamOutlined /></div>
            <div className="stat-text">
              <b>{stats.total}</b>
              <span>TOTAL USERS</span>
            </div>
          </div>
          <div className="stat-item admin">
            <div className="stat-icon-wrap"><CrownOutlined /></div>
            <div className="stat-text">
              <b>{stats.admins}</b>
              <span>ADMINS</span>
            </div>
          </div>
          <div className="stat-item developer">
            <div className="stat-icon-wrap"><CodeOutlined /></div>
            <div className="stat-text">
              <b>{stats.developers}</b>
              <span>DEVELOPERS</span>
            </div>
          </div>
          <div className="stat-item active">
            <div className="stat-icon-wrap"><CheckCircleOutlined /></div>
            <div className="stat-text">
              <b>{stats.active}</b>
              <span>ACTIVE</span>
            </div>
          </div>
        </div>

        {developerExists && (
          <div className="dev-created-banner">
            ✅ Developer already created (1/1) — Only 1 developer allowed
          </div>
        )}
      </div>

      {/* ============ ROLE FILTER TABS ============ */}
      <div className="role-filter-bar">
        <div className="filter-tabs">
          <button className={`filter-tab ${roleFilter === "all" ? "active" : ""}`} onClick={() => setRoleFilter("all")}>
            <TeamOutlined /> All <span className="tab-count">{stats.total}</span>
          </button>
          <button className={`filter-tab ${roleFilter === "admin" ? "active-admin" : ""}`} onClick={() => setRoleFilter("admin")}>
            <CrownOutlined /> Admins <span className="tab-count">{stats.admins}</span>
          </button>
          <button className={`filter-tab ${roleFilter === "developer" ? "active-dev" : ""}`} onClick={() => setRoleFilter("developer")}>
            <CodeOutlined /> Developers <span className="tab-count">{stats.developers}</span>
          </button>
        </div>
      </div>

      {/* ============ TABLE / MOBILE CARDS ============ */}
      {isMobile ? (
        <div className="mobile-admin-list">
          {filtered.length === 0 ? (
            <div className="empty-mobile">
              <UsergroupAddOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />
              <p>No users found</p>
            </div>
          ) : (
            filtered.map(renderMobileCard)
          )}
        </div>
      ) : (
        <div className="table-card">
          <Table
            rowKey="id"
            columns={columns}
            dataSource={filtered}
            loading={loading}
            pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (total) => `Total ${total} users` }}
            scroll={{ x: 1000 }}
          />
        </div>
      )}

      {/* ============ ADD DEVELOPER MODAL ============ */}
      <Modal
        title={<div className="modal-header-title"><CodeOutlined style={{ color: '#8b5cf6' }} /><span>Add New Developer</span></div>}
        open={addModalOpen}
        onCancel={() => { setAddModalOpen(false); form.resetFields(); }}
        footer={null}
        width={isMobile ? '95%' : 550}
        destroyOnHidden
      >
        <div className="dev-note-banner">
          💻 <strong>Note:</strong> Only 1 Developer can be created in the system.
        </div>

        <Form form={form} layout="vertical" onFinish={handleAddUser}>
          <Form.Item name="first_name" label="Full Name" rules={[{ required: true, message: 'Name required' }]}>
            <Input prefix={<UserOutlined />} placeholder="e.g. Shubhanshu Nayak" size="large" />
          </Form.Item>

          <Form.Item name="username" label="Username" rules={[
            { required: true, message: 'Username required' },
            { min: 4, max: 20, message: '4-20 characters' },
            { pattern: /^[a-z0-9_]+$/, message: 'Only lowercase, numbers, underscore' }
          ]}>
            <Input prefix={<UserOutlined />} placeholder="e.g. shubhanshu2026" size="large" />
          </Form.Item>

          <Form.Item name="password" label="Password" rules={[
            { required: true, message: 'Password required' },
            { min: 6, message: 'Min 6 characters' }
          ]}>
            <Input.Password prefix={<LockOutlined />} placeholder="Strong password" size="large" autoComplete="new-password" />
          </Form.Item>

          <Form.Item name="email" label="Email" rules={[{ type: 'email', message: 'Invalid email' }]}>
            <Input prefix={<MailOutlined />} placeholder="user@email.com" size="large" />
          </Form.Item>

          <Form.Item name="contact" label="Contact" rules={[{ pattern: /^[0-9]{10}$/, message: 'Must be 10 digits' }]} normalize={(v) => v?.replace(/\D/g, '').slice(0, 10)}>
            <Input prefix={<PhoneOutlined />} placeholder="10-digit mobile" size="large" maxLength={10} />
          </Form.Item>

          <div className="modal-actions-row">
            <Button size="large" onClick={() => { setAddModalOpen(false); form.resetFields(); }}>Cancel</Button>
            <Button type="primary" htmlType="submit" size="large" loading={addLoading} icon={<CodeOutlined />}
              style={{ background: '#8b5cf6', borderColor: '#8b5cf6' }}>
              Create Developer
            </Button>
          </div>
        </Form>
      </Modal>

      {/* ============ VIEW MODAL ============ */}
      <Modal
        title={<div className="modal-header-title"><EyeOutlined style={{ color: '#ff0055' }} /><span>User Details</span></div>}
        open={viewModalOpen}
        onCancel={() => setViewModalOpen(false)}
        footer={[<Button key="close" onClick={() => setViewModalOpen(false)}>Close</Button>]}
        width={isMobile ? '95%' : 500}
      >
        {selectedUser && (
          <div className="view-modal-content">
            <div className="view-user-header">
              <div className="view-avatar" style={{
                background: selectedUser.role === 'admin' ? 'linear-gradient(135deg, #ff0055, #ff4d88)' : 'linear-gradient(135deg, #8b5cf6, #a78bfa)'
              }}>
                {selectedUser.first_name?.[0] || selectedUser.username?.[0] || "U"}
              </div>
              <h3>{selectedUser.first_name || "N/A"}</h3>
              <p>@{selectedUser.username}</p>
              {selectedUser.role === 'admin'
                ? <Tag icon={<CrownOutlined />} color="magenta" style={{ marginTop: 8, padding: '4px 12px' }}>Admin</Tag>
                : <Tag icon={<CodeOutlined />} color="purple" style={{ marginTop: 8, padding: '4px 12px' }}>Developer</Tag>}
            </div>

            <div className="view-info-grid">
              <div className="view-info-item">
                <div className="view-label">PROFILE ID</div>
                <div className="view-value mono">{selectedUser.profile_id || 'N/A'}</div>
              </div>
              <div className="view-info-item">
                <div className="view-label">STATUS</div>
                <div className="view-value">
                  <Tag color={selectedUser.is_active ? "green" : "orange"}>{selectedUser.is_active ? "Active" : "Inactive"}</Tag>
                </div>
              </div>
              <div className="view-info-item full-width">
                <div className="view-label">EMAIL</div>
                <div className="view-value">{selectedUser.email || 'N/A'}</div>
              </div>
              <div className="view-info-item">
                <div className="view-label">CONTACT</div>
                <div className="view-value">{selectedUser.contact || 'N/A'}</div>
              </div>
              <div className="view-info-item">
                <div className="view-label">REFERRAL</div>
                <div className="view-value">{selectedUser.refer || 'N/A'}</div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default AdminUsers;